import PropTypes from 'prop-types';
import { forwardRef, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import Badge from 'shopper/components/Badge';
import RadioButton from 'shopper/components/RadioButton';

import Loading from 'components/Sidebar/Loading';
import SidebarBox from 'components/Sidebar/SidebarBox';

import { sendEvent } from 'lib/gtag';
import { toCleanedSearchResultsPage } from 'lib/links';
import { toPtBrDecimalNumber } from 'lib/number';

import {
  SEARCH_RESULTS_TYPES,
  SEARCH_RESULTS_TYPE_PARAM_TO_COUNT_KEY,
} from 'constants/searchResults';

const Filters = forwardRef(
  (
    {
      className,
      gaEventAction = null,
      gaEventCategory,
      isLoadingResults = true,
      hasFilters,
      resultCounters = null,
      searchQuery = null,
      searchResultsType = null,
      title,
      ...rest
    },
    ref
  ) => {
    const resultFilters = useMemo(() => {
      const result = [];

      if (isLoadingResults || !hasFilters) {
        return result;
      }

      for (const key in SEARCH_RESULTS_TYPES) {
        const { NAME, PARAM, QUERY } = SEARCH_RESULTS_TYPES[key];

        if (PARAM === 'alias' || PARAM === null) {
          continue;
        }

        const count =
          PARAM === 'general'
            ? resultCounters.total
            : resultCounters[SEARCH_RESULTS_TYPE_PARAM_TO_COUNT_KEY[PARAM]];

        if (
          (count === 0 && PARAM === SEARCH_RESULTS_TYPES.ACTIVE_OFFERS.PARAM) ||
          !!count
        ) {
          result.push({
            count,
            name: NAME,
            type: PARAM,
            query: QUERY,
          });
        }
      }

      return result;
    }, [
      hasFilters,
      isLoadingResults,
      resultCounters,
      searchResultsType?.PARAM,
    ]);

    if (!isLoadingResults && !hasFilters) {
      return null;
    }

    return (
      <SidebarBox
        ref={ref}
        className={twMerge('md:pb-4', className)}
        title={title}
        full
        {...rest}
      >
        {isLoadingResults && <Loading className="h-[150px]" />}
        {!isLoadingResults &&
          resultFilters.map(({ count, name, type, query }) => (
            <div
              key={type}
              className="mt-4 flex items-center justify-between xl:mt-1"
            >
              {/* These need an unique id so it can be rendered more than once
          in an unique render tree, `gaEventAction` will do for now */}
              <RadioButton
                checked={searchResultsType?.PARAM === type}
                disabled={count === 0}
                id={`${gaEventAction}${type}`}
                label={name}
                name={`${gaEventAction}${name}`}
                value={query}
                onChange={() => {
                  sendEvent({
                    action: gaEventAction,
                    category: gaEventCategory,
                    data: name,
                  });
                  toCleanedSearchResultsPage(type, searchQuery);
                }}
              />
              <Badge shape="number" type="neutral">
                {toPtBrDecimalNumber(count)}
              </Badge>
            </div>
          ))}
      </SidebarBox>
    );
  }
);

Filters.displayName = 'Filters';

Filters.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventAction: PropTypes.string,
  gaEventCategory: PropTypes.string.isRequired,
  isLoadingResults: PropTypes.bool,
  hasFilters: PropTypes.bool.isRequired,
  resultCounters: PropTypes.shape({
    activeOffers: PropTypes.number.isRequired,
    finishedOffers: PropTypes.number.isRequired,
    coupons: PropTypes.number.isRequired,
    articles: PropTypes.number.isRequired,
    threads: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    users: PropTypes.number.isRequired,
  }),
  searchQuery: PropTypes.string,
  searchResultsType: PropTypes.shape({
    NAME: PropTypes.string.isRequired,
    QUERY: PropTypes.string.isRequired,
    TYPE: PropTypes.string,
    BUTTON_TEXT: PropTypes.string,
    GA_EVENT_ACTION_PREFIX: PropTypes.string,
    GENERAL_RESULTS_LIMIT: PropTypes.number.isRequired,
    SORT_OPTIONS: PropTypes.arrayOf(
      PropTypes.shape({
        isDefaultOption: PropTypes.bool.isRequired,
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      })
    ),
  }),
  title: PropTypes.string.isRequired,
};

export default Filters;
